import * as React from 'react';
import { ChevronsUpDownIcon, XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { type ComboboxOption } from './combobox-list';

export interface ResolvedSelection {
  set: ReadonlySet<string>;
  list: readonly ComboboxOption[];
}

export interface SelectHandlers {
  onPick: (value: string) => void;
  onClear: () => void;
  onRemove: (value: string) => void;
}

export function resolveSelection(
  value: string | undefined | readonly string[],
  options: readonly ComboboxOption[],
): ResolvedSelection {
  if (typeof value === 'string') {
    const opt = options.find((o) => o.value === value) ?? { value, label: value };
    return { set: new Set([value]), list: [opt] };
  }
  if (value === undefined) return { set: new Set(), list: [] };
  const set = new Set<string>(value);
  const list = Array.from(set).map(
    (v) => options.find((o) => o.value === v) ?? { value: v, label: v },
  );
  return { set, list };
}

// `getSet` is a getter (not a captured value) so toggles read live state when RHF skips a re-render after clear.
export function multiHandlers(
  onChange: (next: string[]) => void,
  onSearchChange: ((q: string) => void) | undefined,
  getSet: () => ReadonlySet<string>,
  close: () => void,
): SelectHandlers {
  const resetSearch = () => {
    if (onSearchChange !== undefined) onSearchChange('');
  };
  return {
    onPick: (value) => {
      const next = new Set(getSet());
      if (next.has(value)) next.delete(value);
      else next.add(value);
      onChange(Array.from(next));
      resetSearch();
    },
    onClear: () => {
      onChange([]);
      close();
    },
    onRemove: (value) => {
      const next = new Set(getSet());
      next.delete(value);
      onChange(Array.from(next));
    },
  };
}

export function singleHandlers(
  onChange: (next: string | undefined) => void,
  close: () => void,
): SelectHandlers {
  return {
    onPick: (value) => {
      onChange(value);
      close();
    },
    onClear: () => {
      onChange(undefined);
      close();
    },
    onRemove: () => {
      onChange(undefined);
    },
  };
}

interface MultiTriggerArgs {
  open: boolean;
  selected: readonly ComboboxOption[];
  placeholder: string;
  invalid: boolean;
  disabled: boolean | undefined;
  id: string | undefined;
  describedBy: string | undefined;
  className: string | undefined;
  onRemove: (value: string) => void;
}

export function renderMultiTrigger(args: Readonly<MultiTriggerArgs>): React.JSX.Element {
  const { open, selected, placeholder, invalid, disabled, id, describedBy, className, onRemove } =
    args;
  return (
    <div
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-invalid={invalid}
      aria-describedby={describedBy}
      aria-disabled={disabled}
      id={id}
      tabIndex={disabled === true ? -1 : 0}
      data-slot="combobox-multi-trigger"
      className={cn(
        'border-input bg-background ring-offset-background flex min-h-10 w-full cursor-pointer items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        'hover:bg-accent hover:text-accent-foreground',
        disabled === true && 'pointer-events-none opacity-50',
        invalid && 'border-destructive ring-2 ring-destructive/40 ring-offset-2',
        className,
      )}
    >
      {selected.length === 0 ? (
        <span className="text-muted-foreground truncate">{placeholder}</span>
      ) : (
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {selected.map((opt) => (
            <ComboboxChip key={opt.value} option={opt} onRemove={onRemove} />
          ))}
        </div>
      )}
      <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" aria-hidden />
    </div>
  );
}

interface ChipProps {
  option: ComboboxOption;
  onRemove: (value: string) => void;
}

function ComboboxChip({ option, onRemove }: Readonly<ChipProps>) {
  const stop = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <span
      data-slot="combobox-chip"
      className="bg-secondary text-secondary-foreground inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs"
    >
      <span className="max-w-32 truncate">{option.label}</span>
      <span
        role="button"
        tabIndex={0}
        aria-label={`Remove ${option.label}`}
        className="hover:text-destructive focus-visible:ring-ring rounded-sm focus-visible:ring-1 focus-visible:outline-none"
        onPointerDown={stop}
        onClick={(e) => {
          stop(e);
          onRemove(option.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            stop(e);
            onRemove(option.value);
          }
        }}
      >
        <XIcon className="size-3" aria-hidden />
      </span>
    </span>
  );
}
